import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Martini, Users, Music, Globe } from "lucide-react";
import barInterior from "@/assets/bar-interior.jpg";

const features = [
  {
    icon: Martini,
    title: "Premium Cocktails",
    description: "Crafted by expert mixologists using the finest spirits",
  },
  {
    icon: Users,
    title: "Social Atmosphere",
    description: "Where strangers become friends over great drinks",
  },
  {
    icon: Music,
    title: "Live Entertainment",
    description: "DJs and live music to set the perfect mood",
  },
  {
    icon: Globe,
    title: "International Vibe",
    description: "A melting pot of cultures in the heart of Bangkok",
  },
];

export function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] rounded-lg overflow-hidden">
              <img
                src={barInterior}
                alt="Tommy Hour Interior"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
            </div>
            {/* Floating accent card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="absolute -bottom-6 -right-6 md:right-6 glass p-6 rounded-lg max-w-[200px]"
            >
              <p className="font-display text-4xl text-primary">5+</p>
              <p className="text-sm text-foreground/70">Years of unforgettable nights</p>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-primary font-medium tracking-[0.2em] text-sm mb-4">
              ABOUT US
            </p>
            <h2 className="section-heading text-foreground mb-6">
              MORE THAN<br />
              <span className="text-gradient-gold">A BAR</span>
            </h2>
            <p className="text-foreground/70 text-lg leading-relaxed mb-6">
              Tommy Hour is not just a bar — it's a moment. A place where the
              city's pulse meets premium spirits, where every pour tells a story,
              and every night becomes a memory.
            </p>
            <p className="text-foreground/60 leading-relaxed mb-10">
              Located in the heart of Bangkok, we've created a sanctuary for
              those who appreciate the art of drinking well. Our international
              crowd brings energy, our cocktails bring quality, and our atmosphere
              brings you back, night after night.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                  className="group"
                >
                  <feature.icon className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold text-foreground mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
